import {
  EnfinityPrimaryBtn,
  EnfinitySecondaryBtn,
} from "@/components/design-system/enfinity/EnfinityButton";
import scrollTo from "@/utils/scrollTo";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowDownRight, ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import YoutubeIconSVG from "@/assets/svg/youtube-icon.svg";
import CryptoIconSVG from "@/assets/svg/creator-shield/crypto.svg";
import MicrosoftIconSVG from "@/assets/svg/microsoft-icon.svg";
import SnapchatIconSVG from "@/assets/svg/snapchat-icon.svg";
import BadgeCheckSVG from "@/assets/svg/badge-check.svg";
export default function Header() {
  const [showRest, setShowRest] = useState(false);
  const [currentBadgeIndex, setCurrentBadgeIndex] = useState(0);

  const badges = [
    {
      name: "Official YouTube Certified",
      icon: YoutubeIconSVG,
      link: "https://servicesdirectory.withyoutube.com/directory/enfinity-talent-agency/",
    },
    {
      name: "Microsoft Certified Partner",
      icon: MicrosoftIconSVG,
      link: "#",
    },
    {
      name: "Official Snap Partner",
      icon: SnapchatIconSVG,
      link: "#",
    },
    {
      name: "Crypto Payout Agency",
      icon: CryptoIconSVG,
      link: "#",
    },
  ];

  useEffect(() => {
    const timer = setTimeout(() => setShowRest(true), 800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBadgeIndex((prev) => (prev + 1) % badges.length);
    }, 3000); // Change badge every 3 seconds

    return () => clearInterval(interval);
  }, [badges.length]);

  // Staggered animation variants for smooth reveal
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.4,
      },
    },
  };

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      y: 30,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 2,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const textVariants = {
    hidden: { 
      opacity: 0, 
      y: 40,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1.5,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  // Letter variants for stagger animation
  const letterVariants = {
    hidden: { 
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.2,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  // Container variants for staggering letters
  const headingSpanContainerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.02,
        delayChildren: 0.05,
      },
    },
  };

  const restVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 45,
        damping: 8,
        mass: 1.2,
        duration: 4,
      },
    },
  };

  const current = badges[currentBadgeIndex];

  return (
    <div className="relative">
      <div className="bg-[#1a1a1a] overflow-hidden relative lg:pt-12 xl:pt-4 2xl:pt-12 4xl:pt-16">
         {/* Background Image */}
      <motion.div
        className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat z-[5] opacity-80"
        style={{
          backgroundImage: `url('https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/aboutus-bg.png')`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
        }}
        initial={{ opacity: 0, scale: 1.1 }}
        animate={{ opacity: 0.8, scale: 1 }}
        transition={{ duration: 3.5, ease: [0.22, 1, 0.36, 1] }}
      />

        {/* Dark Overlay */}
        <motion.div 
          className="absolute inset-0 w-full h-full bg-black/40 z-[2]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2.5, ease: [0.22, 1, 0.36, 1] }}
        ></motion.div>

        <div className="relative 5xl:max-w-[100rem] mx-auto max-w-7xl z-10">
          <div className="lg:mt-6 mt-8 xxs:mt-8 xs:mt-8 sm:mt-8 md:mt-8 4xl:mt-0 min-h-[970px] xxs:min-h-[800px] xs:min-h-[890px] sm:min-h-[900px] md:min-h-[800px] lg:min-h-[calc(100vh-5px)] xl:min-h-[calc(100vh-180px)] 1xl:min-h-[calc(100vh-280px)] 2xl:min-h-[calc(100vh-100px)] lg:h-screen 4xl:max-h-[calc(100vh-250px)] flex flex-col items-center justify-center ">
            {/* Content */}
            <motion.div 
              className="flex flex-col w-full items-center justify-center text-center px-6 lg:px-12 xl:px-12 z-10 gap-3 4xl:gap-7"
              variants={containerVariants}
              initial="hidden"
              animate={showRest ? "visible" : "hidden"}
            >
              {/* Badge with Animation */}
              <motion.div
                variants={itemVariants}
                className="mb-6 pt-8 xxs:pt-12 xs:pt-20 sm:pt-20 md:pt-8 lg:pt-16"
              >
                <div className="flex justify-center items-center z-20 w-full">
                  <a
                    href={current.link || "#"}
                    target={current.link && current.link !== "#" ? "_blank" : undefined}
                    rel={current.link && current.link !== "#" ? "noopener noreferrer" : undefined}
                  >
                    <div className="relative h-14 w-fit overflow-hidden">
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={current.name}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.8, ease: "easeInOut" }}
                          className={`absolute flex items-center gap-2 rounded-full ${current.name === "Crypto Payout Agency" || current.name === "Microsoft Certified Partner" ? "py-2" : "py-1"} px-4 border border-white/20 bg-white/5 backdrop-blur-md text-white shimmer-badge`}
                        >
                          <img
                            src={current.icon}
                            alt={current.name}
                            className={
                              current.name === "Crypto Payout Agency" || current.name === "Microsoft Certified Partner" 
                                ? "h-6 w-6" 
                                : current.name === "Official Snap Partner"
                                ? "h-8 w-6"
                                : "h-8 w-8"
                            }
                          />
                          <span className="xxs:text-xs xs:text-sm text-base">{current.name}</span>
                          <img src={BadgeCheckSVG} className="h-5 w-5" alt="Badge" />
                        </motion.div>
                      </AnimatePresence>
                    </div>
                  </a>
                </div>
              </motion.div>

              <motion.h1
                className="text-4xl xxs:text-[55px] 4xl:text-[90px] font-normal mb-6 leading-tight lg:leading-none space-y-0 lg:space-y-4 enfinity-header-font text-center"
              >
                <motion.span 
                  className="block leading-tight lg:leading-none -mb-1 lg:mb-0 text-center text-white"
                  variants={textVariants}
                  initial="hidden"
                  animate={showRest ? "visible" : "hidden"}
                >
                  Built to{" "}
                  <span className="relative inline-block">
                    {/* Stroke layer with vertical gradient mask - behind fill */}
                    <span
                      className="absolute inset-0 text-white z-0"
                      style={{
                        WebkitTextStroke: "2px white",
                        WebkitTextFillColor: "transparent",
                        maskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)",
                        WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)",
                        pointerEvents: "none",
                      }}
                    >
                      scale and empower
                    </span>
                    {/* Fill layer with gradient - on top */}
                    <span className="relative z-10 bg-gradient-to-r from-[#00a656] via-[#3b82f6] to-[#8b5cf6] bg-clip-text text-transparent">
                      scale and empower
                    </span>
                  </span>{" "}
                  <br className="hidden 4xl:block" />
                  creators worldwide
                </motion.span>
              </motion.h1>

              <motion.p
                variants={itemVariants}
                className="text-white/80 text-base xxs:text-[1rem] xs:text-[1rem] sm:text-[1rem] md:text-lg font-light mb-8 4xl:max-w-5xl enfinity-subtext-font"
              >
Enfinity powers modern creator growth by bringing distribution, rights protection, flexible payments, and operational support into one platform.              </motion.p>

              <motion.div
                variants={itemVariants}
                className="flex flex-col sm:flex-row mb-4 w-full xxs:w-auto xs:w-auto sm:w-auto gap-4 sm:gap-4"
              >
                <a href="https://core.enfinity.com/signup" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                  <EnfinitySecondaryBtn name="Create an account" className="w-full sm:w-auto hover:!translate-y-0 hover:scale-105" />
                </a>
              <div className="group relative flex items-center w-full sm:w-auto gap-0 transition-transform duration-300 ease-out hover:scale-105 origin-left">
                <div className="w-full sm:w-auto">
                  <a
                    href="#why-choose-us"
                    className="w-full sm:w-auto xxs:w-auto"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollTo("why-choose-us");
                    }}
                  >
                    <EnfinityPrimaryBtn noHoverAnimation className="w-full sm:w-auto" name="Explore our ecosystem"/>
                  </a>
                </div>
              <div className="hidden xs:block absolute h-5 w-3 bg-[#292929] right-10 !z-40" 
              style={{
                border: '1px solid linear-gradient(to bottom, #808080 0%, rgba(255, 255, 255, 0) 100%)'
              }}></div>
                <div className="hidden xs:block">
                  <EnfinityPrimaryBtn noHoverAnimation className="!w-10 !h-10 sm:w-auto" name={<><ArrowDownRight className="w-5 h-5" /> </>} />
                </div>
                </div>
              </motion.div>

              {/* Trusted By Section */}
              <motion.div
                variants={itemVariants}
                className="flex flex-col items-center justify-center mt-8 mb-6 xxs:mb-[50px] xs:mb-[50px] sm:mb-[50px] md:mb-[-10px] lg:mb-12 xl:mb-2 2xl:mb-8 4xl:mb-12"
              >
                <img
                  src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/creatorss.png"
                  alt="Trusted by creators"
                  className="w-full max-w-sm h-auto object-contain pt-8 xxs:pt-16 xs:pt-16 sm:pt-16 md:pt-16 lg:pt-4 xl:pt-16 4xl:pt-20"
                />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
